import { Fragment, useEffect, useMemo, useState } from 'react';
import {
  BILLETES_COP,
  DENOMINACIONES_COP,
  MONEDAS_COP,
  contarEfectivo,
  contarSolo,
  esRestoIncontable,
  esperadoDeCuenta,
  fechaNegocio,
  formatearCOP,
} from '@tanteo/compartido';
import { abrirCajaRpc, corregirBaseCajaRpc, guardarCuadreRpc, obtenerCuadre, reabrirCuadreRpc, resumenDia } from '../lib/api';
import { alCambiarCola, drenar } from '../lib/cola-offline';
import { listarCuentas, type Cuenta } from '../lib/api-cuentas';
import { useApp } from '../lib/contexto';
import { useLectura } from '../lib/lectura';
import { BarraDePagina, Boton, Campo, CampoLista, CeldaPlata, ErrorDeLectura, Esqueleto, FilaAcciones, FilaTabla, MensajeError, MensajeExito, NumeroEnLinea, TablaAncha, Tarjeta, TextoAyuda } from '../ui/base';
import { Modal } from '../ui/Modal';

type Saldos = Record<string, number>;

/** El extremo que no es una cuenta del negocio: la plata que no está en el mostrador. */
const CAJA_GENERAL = 'caja-general';

/** Un traslado mientras se escribe: el monto puede quedar vacío, que no es lo mismo que cero. */
interface FilaTraslado {
  de: string;
  a: string;
  monto: number | null;
}

/**
 * El tono de cada denominación, en clases completas: Tailwind solo genera las que ve escritas. En
 * las monedas el borde es el aro y el fondo el centro, que distingue a las dos bimetálicas.
 */
const COLOR_DENOMINACION: Record<number, string> = {
  100000: 'border-plata-verde bg-plata-verde/10',
  50000: 'border-plata-violeta bg-plata-violeta/10',
  20000: 'border-plata-naranja bg-plata-naranja/10',
  10000: 'border-plata-rojo bg-plata-rojo/10',
  5000: 'border-plata-cafe bg-plata-cafe/10',
  2000: 'border-plata-azul bg-plata-azul/10',
  1000: 'border-plata-dorado bg-plata-moneda/10',
  500: 'border-plata-moneda bg-plata-dorado/10',
  200: 'border-plata-moneda bg-plata-moneda/10',
  100: 'border-plata-bronce bg-plata-bronce/10',
  50: 'border-plata-moneda bg-plata-moneda/10',
};

export function Cuadre() {
  const { negocio } = useApp();
  const esCajero = negocio?.rol === 'cajero';
  const [fecha, setFecha] = useState(fechaNegocio());
  const [traslados, setTraslados] = useState<FilaTraslado[]>([]);
  const [conteo, setConteo] = useState<Record<string, number>>({});
  const [saldosIniciales, setSaldosIniciales] = useState<Saldos>({});
  const [saldosFinales, setSaldosFinales] = useState<Saldos>({});
  const [estado, setEstado] = useState<'abierto' | 'cerrado'>('abierto');
  const [reabriendo, setReabriendo] = useState(false);
  const [corrigiendoBase, setCorrigiendoBase] = useState(false);
  const [baseNueva, setBaseNueva] = useState<number | null>(null);
  const [motivoBase, setMotivoBase] = useState('');
  const [errorBase, setErrorBase] = useState('');
  const [motivoReapertura, setMotivoReapertura] = useState('');
  const [errorReapertura, setErrorReapertura] = useState('');
  // El éxito y el error son dos estados distintos, con su propio color: un cierre que falla no
  // puede parecerse a uno que funcionó.
  const [exito, setExito] = useState('');
  const [error, setError] = useState('');
  const [ocupado, setOcupado] = useState(false);
  // Las ventas de este equipo que esperan subir: con alguna pendiente el día no se cierra, porque
  // llegaría después del cierre y la base la rechazaría.
  const [sinSubir, setSinSubir] = useState(0);
  useEffect(() => alCambiarCola((c) => setSinSubir(c.pendientes)), []);

  // Tres lecturas con su propio error (A4): si una falla no se sabe si el día está abierto, y la
  // pantalla no ofrece abrirlo ni pinta una tabla a medias.
  const negocioId = negocio?.negocioId ?? '';
  const lecturaCuadre = useLectura(() => (negocioId ? obtenerCuadre(negocioId, fecha) : Promise.resolve(null)), `${negocioId}|${fecha}`);
  const lecturaDia = useLectura(() => (negocioId ? resumenDia(negocioId, fecha) : Promise.resolve(null)), `${negocioId}|${fecha}`);
  const lecturaCuentas = useLectura(() => (negocioId ? listarCuentas(negocioId) : Promise.resolve([])), negocioId);
  const cuadre = lecturaCuadre.datos;
  const cuentas = lecturaCuentas.datos ?? [];
  const resumen = lecturaDia.datos?.cuentas ?? [];
  const gastosCajaGeneral = lecturaDia.datos?.gastosCajaGeneral ?? 0;
  const gastosPorCuenta = lecturaDia.datos?.gastosPorCuenta ?? {};
  const abierto = cuadre !== null && cuadre !== undefined;
  const cargando = lecturaCuadre.cargando || lecturaDia.cargando || lecturaCuentas.cargando;
  const fallaLectura = Boolean(lecturaCuadre.error || lecturaDia.error || lecturaCuentas.error);

  useEffect(() => {
    if (cuadre === undefined) return;
    setTraslados(cuadre ? cuadre.traslados.map((t) => ({ de: t.de, a: t.a, monto: t.monto })) : []);
    setConteo(cuadre ? cuadre.conteo : {});
    setSaldosIniciales(cuadre ? cuadre.saldosIniciales : {});
    setSaldosFinales(cuadre ? cuadre.saldosFinales : {});
    setEstado(cuadre ? cuadre.estado : 'abierto');
  }, [cuadre]);

  // `contarEfectivo` suma igual que `guardar_cuadre` en el servidor, que es quien manda: un pgTAP
  // cruza las dos implementaciones.
  const contado = useMemo(() => contarEfectivo(conteo), [conteo]);
  const ventasDe = (cuentaId: string) => resumen.find((r) => r.cuentaId === cuentaId)?.total ?? 0;
  const gastosDe = (cuentaId: string) => gastosPorCuenta[cuentaId] ?? 0;
  // Un traslado mueve plata entre dos sitios, así que cuenta en los dos: baja donde sale y sube donde
  // entra. Solo entran los que ya tienen monto: una fila a medio escribir no mueve nada.
  const trasladosConMonto = traslados.filter((t) => (t.monto ?? 0) > 0);
  const salidasDe = (id: string) =>
    trasladosConMonto.filter((t) => t.de === id).reduce((suma, t) => suma + (t.monto ?? 0), 0);
  const entradasDe = (id: string) =>
    trasladosConMonto.filter((t) => t.a === id).reduce((suma, t) => suma + (t.monto ?? 0), 0);
  const esperadoDe = (c: Cuenta) =>
    esperadoDeCuenta({
      saldoInicial: saldosIniciales[c.id] ?? 0,
      ventas: ventasDe(c.id),
      gastos: gastosDe(c.id),
      salidas: salidasDe(c.id),
      entradas: entradasDe(c.id),
    });
  const gastosDelMostrador = cuentas.filter((c) => c.esEfectivo).reduce((suma, c) => suma + gastosDe(c.id), 0);
  const declaradoDe = (c: Cuenta) => (c.esEfectivo ? contado : (saldosFinales[c.id] ?? 0));
  /**
   * Esa cuenta todavía no se ha declarado. Su diferencia sí se calcula y sí entra en el total; la
   * marca solo dice de dónde viene el hueco.
   */
  const sinDeclarar = (c: Cuenta) => !c.esEfectivo && saldosFinales[c.id] === undefined;
  const diferenciaTotal = cuentas.reduce((suma, c) => suma + (declaradoDe(c) - esperadoDe(c)), 0);
  // El resto que ninguna moneda alcanza (A6): se muestra aparte y no pone el día en rojo.
  const restoDe = (c: Cuenta) => {
    const dif = declaradoDe(c) - esperadoDe(c);
    return c.esEfectivo && esRestoIncontable(dif, esperadoDe(c), DENOMINACIONES_COP) ? dif : 0;
  };
  const restoDelDia = cuentas.reduce((suma, c) => suma + restoDe(c), 0);
  const cuadra = diferenciaTotal - restoDelDia === 0;

  /** Los sitios entre los que se puede mover plata: las cuentas del negocio y la caja - general. */
  const extremos = [
    ...cuentas.map((c) => ({
      id: c.id,
      etiqueta: c.esEfectivo ? `${c.nombre} (caja - mostrador)` : c.nombre,
    })),
    { id: CAJA_GENERAL, etiqueta: 'caja - general' },
  ];
  const cuentaDeEfectivo = cuentas.find((c) => c.esEfectivo);

  function ponerTraslado(indice: number, parte: Partial<FilaTraslado>) {
    setTraslados((previo) => previo.map((t, i) => (i === indice ? { ...t, ...parte } : t)));
  }

  async function abrir() {
    if (!negocio) return;
    setOcupado(true);
    setExito('');
    setError('');
    try {
      const { yaEstaba } = await abrirCajaRpc(negocio.negocioId, fecha, saldosIniciales);
      setExito(yaEstaba ? 'El día ya estaba abierto.' : 'Día abierto.');
      // Todo el cuadre se vuelve a leer: el día pudo abrirse con conteo y traslados ya guardados.
      lecturaCuadre.recargar();
      lecturaDia.recargar();
      lecturaCuentas.recargar();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo abrir el día.');
    } finally {
      setOcupado(false);
    }
  }

  /** La base de efectivo mal tecleada al abrir se corrige con el día abierto; el motivo queda anotado. */
  async function corregirBase() {
    if (!negocio || !cuentaDeEfectivo) return;
    if (baseNueva === null) {
      setErrorBase('Escribe la base correcta.');
      return;
    }
    if (motivoBase.trim().length < 3) {
      setErrorBase('Escribe por qué se corrige la base.');
      return;
    }
    setOcupado(true);
    setErrorBase('');
    try {
      await corregirBaseCajaRpc(negocio.negocioId, fecha, baseNueva, motivoBase.trim());
      setSaldosIniciales({ ...saldosIniciales, [cuentaDeEfectivo.id]: baseNueva });
      setCorrigiendoBase(false);
      setMotivoBase('');
      setExito('Base de caja corregida.');
    } catch (e) {
      setErrorBase(e instanceof Error ? e.message : 'No se pudo corregir la base.');
    } finally {
      setOcupado(false);
    }
  }

  /**
   * Devolver el día a abierto para poder cambiarlo. Un día pasado que queda abierto vuelve a contar
   * como día sin cuadrar en los pendientes, así que el aviso lo dice cuando la fecha no es la de hoy.
   */
  async function reabrir() {
    if (!negocio) return;
    if (motivoReapertura.trim().length < 3) {
      setErrorReapertura('Escribe por qué se reabre el día.');
      return;
    }
    setOcupado(true);
    setErrorReapertura('');
    try {
      await reabrirCuadreRpc(negocio.negocioId, fecha, motivoReapertura.trim());
      setEstado('abierto');
      setReabriendo(false);
      setMotivoReapertura('');
      setExito(
        fecha < fechaNegocio()
          ? 'Día reabierto. Ya puedes cambiarlo; mientras esté abierto aparece como día sin cuadrar.'
          : 'Día reabierto. Ya puedes cambiarlo y volver a cerrarlo.'
      );
    } catch (e) {
      setErrorReapertura(e instanceof Error ? e.message : 'No se pudo reabrir el día.');
    } finally {
      setOcupado(false);
    }
  }

  async function guardar(cerrar: boolean) {
    if (!negocio) return;
    setOcupado(true);
    setExito('');
    setError('');
    try {
      await guardarCuadreRpc({
        negocioId: negocio.negocioId,
        fecha,
        traslados: trasladosConMonto.map((t) => ({ de: t.de, a: t.a, monto: t.monto ?? 0 })),
        conteo,
        saldosFinales: { ...saldosFinales, ...Object.fromEntries(cuentas.filter((c) => c.esEfectivo).map((c) => [c.id, contado])) },
        cerrar,
      });
      if (cerrar) setEstado('cerrado');
      setExito(cerrar ? 'Día cerrado.' : 'Cuadre guardado.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo guardar el cuadre.');
    } finally {
      setOcupado(false);
    }
  }

  if (!negocio) return null;
  if (cargando) return <Esqueleto lineas={8} />;

  return (
    <div className="space-y-4">
      <BarraDePagina titulo="Cierre del día">
        {!esCajero && (
          <div className="w-48">
            <Campo etiqueta="Fecha" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
          </div>
        )}
      </BarraDePagina>

      <MensajeExito e2e="cuadre-exito">{exito}</MensajeExito>
      <MensajeError caja e2e="cuadre-error">{error}</MensajeError>

      {fallaLectura ? (
        <div className="space-y-2">
          <ErrorDeLectura que="el cuadre del día" error={lecturaCuadre.error} onReintentar={lecturaCuadre.recargar} e2e="cuadre-error-lectura" />
          <ErrorDeLectura que="las ventas del día" error={lecturaDia.error} onReintentar={lecturaDia.recargar} e2e="cuadre-error-ventas" />
          <ErrorDeLectura que="las cuentas del negocio" error={lecturaCuentas.error} onReintentar={lecturaCuentas.recargar} e2e="cuadre-error-cuentas" />
        </div>
      ) : !abierto ? (
        <Tarjeta>
          <h2 className="mb-1 font-extrabold">Abre el día</h2>
          <TextoAyuda>
            Anota con cuánto empiezas en cada cuenta. Al cerrar comparamos contra lo que quedó.
          </TextoAyuda>
          <div className="space-y-2">
            {cuentas.map((c) => (
              <label key={c.id} className="flex items-center justify-between gap-3">
                <span className="font-semibold">{c.nombre}</span>
                <NumeroEnLinea
                  ancho="largo"
                  plata
                  min={0}
                  valor={saldosIniciales[c.id] ?? null}
                  onValor={(n) => setSaldosIniciales({ ...saldosIniciales, [c.id]: n ?? 0 })}
                />
              </label>
            ))}
          </div>
          <div className="mt-4 flex justify-end">
            <Boton grande onClick={() => void abrir()} disabled={ocupado} data-e2e="abrir-caja">
              {ocupado ? 'Abriendo…' : 'ABRIR EL DÍA'}
            </Boton>
          </div>
        </Tarjeta>
      ) : (
        <>
          <Tarjeta>
            <h2 className="mb-2 font-extrabold">1 · Ventas por cuenta</h2>
            <TablaAncha className="text-contenido">
              <thead>
                <tr className="text-muted border-border border-b text-etiqueta uppercase">
                  <th className="py-2 pr-2">Cuenta</th>
                  <th className="py-2 pr-2 text-right">Al abrir</th>
                  <th className="py-2 pr-2 text-right">Ventas</th>
                  <th className="py-2 pr-2 text-right">Gastos</th>
                  <th className="py-2 pr-2 text-right">Debería haber</th>
                  <th className="bg-surface py-2 pr-2 text-right">Hay</th>
                  <th className="py-2 pr-2 text-right">Diferencia</th>
                </tr>
              </thead>
              <tbody>
                {cuentas.map((c) => {
                  const esperado = esperadoDe(c);
                  const declarado = declaradoDe(c);
                  const dif = declarado - esperado;
                  const resto = restoDe(c);
                  return (
                    <Fragment key={c.id}>
                    <FilaTabla data-e2e="fila-cuenta">
                      <td className="py-2 pr-2 font-bold">
                        {c.nombre}
                        {c.esEfectivo && <span className="text-muted ml-1 text-etiqueta">(contado)</span>}
                      </td>
                      <td className="py-2 pr-2 text-right">
                        <span className="num">{formatearCOP(saldosIniciales[c.id] ?? 0)}</span>
                        {c.esEfectivo && !esCajero && estado === 'abierto' && (
                          <Boton variante="contorno" className="ml-2" onClick={() => setCorrigiendoBase(true)} data-e2e="corregir-base">
                            ✎ corregir
                          </Boton>
                        )}
                      </td>
                      <CeldaPlata>{formatearCOP(ventasDe(c.id))}</CeldaPlata>
                      <CeldaPlata>{formatearCOP(gastosDe(c.id))}</CeldaPlata>
                      <CeldaPlata>{formatearCOP(esperado)}</CeldaPlata>
                      <td className="bg-surface py-2 pr-2 text-right">
                        {c.esEfectivo ? (
                          <span className="num">{formatearCOP(declarado)}</span>
                        ) : (
                          <NumeroEnLinea
                            ancho="dinero"
                            plata
                            min={0}
                            valor={saldosFinales[c.id] ?? null}
                            disabled={estado === 'cerrado'}
                            onValor={(n) =>
                              setSaldosFinales(
                                n === null
                                  ? Object.fromEntries(Object.entries(saldosFinales).filter(([k]) => k !== c.id))
                                  : { ...saldosFinales, [c.id]: n }
                              )
                            }
                            data-e2e={`declarado-${c.id}`}
                          />
                        )}
                      </td>
                      <td className="py-2 pr-2 text-right" data-e2e={`diferencia-${c.id}`}>
                        {sinDeclarar(c) && <span className="text-muted block text-etiqueta">sin declarar</span>}
                        <span className={`num font-bold ${dif === 0 ? 'text-success' : resto !== 0 ? 'text-muted' : 'text-destructive'}`}>
                          {formatearCOP(dif)}
                        </span>
                      </td>
                    </FilaTabla>
                    {resto !== 0 && (
                      <tr data-e2e="resto-incontable">
                        <td colSpan={7} className="text-muted pb-2 pr-2 text-right text-etiqueta">
                          ⓘ {formatearCOP(-resto)} no se pueden pagar con monedas: no cuentan como faltante
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  );
                })}
              </tbody>
            </TablaAncha>
            {corrigiendoBase && cuentaDeEfectivo && (
              <div className="border-border mt-3 flex flex-wrap items-end gap-3 border-t pt-3" data-e2e="base-editor">
                <p className="pb-2 text-contenido font-semibold">
                  Al abrir <span className="num">{formatearCOP(saldosIniciales[cuentaDeEfectivo.id] ?? 0)}</span> →
                </p>
                <NumeroEnLinea
                  ancho="dinero"
                  plata
                  min={0}
                  aria-label="Base de caja correcta"
                  valor={baseNueva}
                  onValor={setBaseNueva}
                  data-e2e="base-nueva"
                />
                <Campo
                  etiqueta="Motivo"
                  className="w-72"
                  value={motivoBase}
                  placeholder="Se tecleó un cero de más"
                  onChange={(e) => setMotivoBase(e.target.value)}
                  data-e2e="base-motivo"
                />
                <Boton onClick={() => void corregirBase()} disabled={ocupado} data-e2e="base-guardar">
                  {ocupado ? 'Guardando…' : 'Guardar'}
                </Boton>
                <Boton variante="contorno" onClick={() => setCorrigiendoBase(false)} disabled={ocupado}>
                  Cancelar
                </Boton>
                <MensajeError className="w-full" e2e="base-error">{errorBase}</MensajeError>
              </div>
            )}
          </Tarjeta>

          <Tarjeta>
            <h2 className="mb-2 font-extrabold">2 · Conteo del efectivo</h2>
            {/* Dos montones, como está la plata en la caja - mostrador, y cada fila con el color de su
                denominación: contar en once casillas iguales obliga a leer la cifra en cada una. */}
            <div className="grid gap-4 lg:grid-cols-2">
              {[
                { titulo: 'Billetes', denominaciones: BILLETES_COP, e2e: 'billetes' },
                { titulo: 'Monedas', denominaciones: MONEDAS_COP, e2e: 'monedas' },
              ].map((grupo) => (
                <div key={grupo.titulo}>
                  <p className="text-muted mb-1 text-etiqueta font-bold uppercase">{grupo.titulo}</p>
                  <div className="space-y-1">
                    {grupo.denominaciones.map((d) => (
                      <label
                        key={d}
                        className={`rounded-control grid grid-cols-3 items-center gap-2 border-l-4 px-2 py-1 ${COLOR_DENOMINACION[d] ?? ''}`}
                        data-e2e={`fila-${d}`}
                      >
                        <span className="num text-contenido font-bold">{formatearCOP(d)}</span>
                        <NumeroEnLinea
                          ancho="lleno"
                          alineacion="centro"
                          min={0}
                          aria-label={`Cantidad de ${formatearCOP(d)}`}
                          valor={conteo[String(d)] ?? null}
                          disabled={estado === 'cerrado'}
                          onValor={(n) =>
                            setConteo((previo) => {
                              const siguiente = { ...previo };
                              if (n === null || n === 0) delete siguiente[String(d)];
                              else siguiente[String(d)] = n;
                              return siguiente;
                            })
                          }
                        />
                        <span className="num text-right text-contenido font-semibold" data-e2e={`subtotal-de-${d}`}>
                          {formatearCOP(d * (conteo[String(d)] ?? 0))}
                        </span>
                      </label>
                    ))}
                  </div>
                  <p className="text-muted mt-1 flex justify-between text-contenido font-semibold">
                    <span>Subtotal {grupo.titulo.toLowerCase()}</span>
                    <span className="num" data-e2e={`subtotal-${grupo.e2e}`}>
                      {formatearCOP(contarSolo(conteo, grupo.denominaciones))}
                    </span>
                  </p>
                </div>
              ))}
            </div>
            <p className="border-border mt-6 flex justify-between border-t pt-4 text-cifra font-extrabold">
              <span>Efectivo contado</span>
              <span className="num" data-e2e="contado">
                {formatearCOP(contado)}
              </span>
            </p>
          </Tarjeta>

          <Tarjeta>
            <h2 className="mb-2 font-extrabold">3 · Traslados y gastos</h2>
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-contenido font-semibold">Traslados</span>
              {estado !== 'cerrado' && (
                <Boton
                  variante="contorno"
                  onClick={() =>
                    setTraslados([
                      ...traslados,
                      { de: cuentaDeEfectivo?.id ?? CAJA_GENERAL, a: CAJA_GENERAL, monto: null },
                    ])
                  }
                  data-e2e="traslado-agregar"
                >
                  ＋ Traslado
                </Boton>
              )}
            </div>
            <TextoAyuda>
              Plata que pasa de una caja o cuenta a otra. No es un gasto: los gastos ya están restados
              abajo.
            </TextoAyuda>
            {traslados.length > 0 && (
              <div className="mt-2 space-y-2">
                {traslados.map((t, i) => (
                  <div key={i} className="flex flex-wrap items-end gap-2">
                    <CampoLista
                      etiqueta={i === 0 ? 'De' : undefined}
                      className="w-64"
                      value={t.de}
                      disabled={estado === 'cerrado'}
                      onChange={(e) => ponerTraslado(i, { de: e.target.value })}
                      data-e2e={`traslado-de-${i}`}
                    >
                      {extremos.map((x) => (
                        <option key={x.id} value={x.id}>{x.etiqueta}</option>
                      ))}
                    </CampoLista>
                    <span className="pb-2 text-contenido font-bold">→</span>
                    <CampoLista
                      etiqueta={i === 0 ? 'A' : undefined}
                      className="w-64"
                      value={t.a}
                      disabled={estado === 'cerrado'}
                      onChange={(e) => ponerTraslado(i, { a: e.target.value })}
                      data-e2e={`traslado-a-${i}`}
                    >
                      {extremos.map((x) => (
                        <option key={x.id} value={x.id}>{x.etiqueta}</option>
                      ))}
                    </CampoLista>
                    <div>
                      {i === 0 && <span className="etiqueta">Monto</span>}
                      <NumeroEnLinea
                        ancho="dinero"
                        plata
                        min={0}
                        valor={t.monto}
                        disabled={estado === 'cerrado'}
                        onValor={(n) => ponerTraslado(i, { monto: n })}
                        data-e2e={`traslado-monto-${i}`}
                      />
                    </div>
                    {estado !== 'cerrado' && (
                      <Boton
                        variante="contorno"
                        onClick={() => setTraslados(traslados.filter((_, j) => j !== i))}
                        data-e2e={`traslado-quitar-${i}`}
                      >
                        ✕
                      </Boton>
                    )}
                  </div>
                ))}
              </div>
            )}
            <div className="text-muted mt-3 space-y-0.5 text-contenido">
              <p className="flex justify-between" data-e2e="gastos-mostrador">
                <span>Gastos pagados con la caja - mostrador</span>
                <span className="num">{formatearCOP(gastosDelMostrador)} (ya restados)</span>
              </p>
              {gastosCajaGeneral > 0 && (
                <p className="flex justify-between" data-e2e="gastos-caja-general">
                  <span>Gastos pagados con la caja - general</span>
                  <span className="num">{formatearCOP(gastosCajaGeneral)} (no tocan el mostrador)</span>
                </p>
              )}
            </div>
          </Tarjeta>

          <Tarjeta>
            <p
              className={`flex items-baseline justify-between text-cifra font-extrabold ${
                cuadra ? 'text-success' : 'text-destructive'
              }`}
              data-e2e="diferencia"
            >
              <span>Diferencia total</span>
              <span className="num">{formatearCOP(diferenciaTotal)}</span>
            </p>
            <TextoAyuda>
              Debe quedar en $0. Si no, sobra o falta plata: puedes cerrar el día igual y queda
              anotado.
            </TextoAyuda>
          </Tarjeta>

          {sinSubir > 0 && estado !== 'cerrado' && (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <MensajeError caja e2e="cuadre-cola-pendiente">
                ⚠ Este mostrador tiene {sinSubir} venta{sinSubir === 1 ? '' : 's'} sin subir. Conéctate para que
                suban antes de cerrar el día.
              </MensajeError>
              <Boton variante="secundario" onClick={() => void drenar()}>
                Reintentar
              </Boton>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-end gap-3">
            <div className="flex gap-2">
              {estado === 'cerrado' && !esCajero && (
                <Boton variante="contorno" onClick={() => setReabriendo(true)} data-e2e="reabrir-dia">
                  Reabrir el día
                </Boton>
              )}
              <Boton
                variante="exito"
                grande
                onClick={() => void guardar(true)}
                disabled={ocupado || estado === 'cerrado' || sinSubir > 0}
                data-e2e="cerrar-cuadre"
              >
                {estado === 'cerrado' ? 'Día cerrado' : 'CERRAR EL DÍA'}
              </Boton>
              <Boton variante="contorno" onClick={() => void guardar(false)} disabled={ocupado || estado === 'cerrado'}>
                Guardar
              </Boton>
            </div>
          </div>

          <Modal abierto={reabriendo} titulo="Reabrir el día" onCerrar={() => setReabriendo(false)}>
            <div className="space-y-3">
              <TextoAyuda>
                El día vuelve a quedar abierto para poder cambiarlo. Queda anotado quién lo reabrió y por
                qué.
              </TextoAyuda>
              <Campo
                etiqueta="¿Por qué se reabre? *"
                value={motivoReapertura}
                placeholder="Faltó contar la plata de la gaveta"
                onChange={(e) => setMotivoReapertura(e.target.value)}
                data-e2e="reabrir-motivo"
              />
              <MensajeError e2e="reabrir-error">{errorReapertura}</MensajeError>
              <FilaAcciones>
                <Boton variante="contorno" onClick={() => setReabriendo(false)} disabled={ocupado}>
                  Volver
                </Boton>
                <Boton onClick={() => void reabrir()} disabled={ocupado} data-e2e="reabrir-confirmar">
                  {ocupado ? 'Reabriendo…' : 'Reabrir'}
                </Boton>
              </FilaAcciones>
            </div>
          </Modal>
        </>
      )}
    </div>
  );
}
